import { REMARK_SOURCES, type RemarkSource, type SupervisionSettings } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { ErrorBox, toast } from '../../ui/feedback';
import { Checkbox } from '../../ui/form';
import { Panel } from '../../ui/layout';

/**
 * Видимость источников замечаний в компании (УЧ-07): внутренние источники не видят подрядчик, технадзор и
 * проектировщик. Меняет тот, кто выдаёт замечания по всей компании; уже выданные замечания не меняются.
 */

type View = SupervisionSettings & { canEdit: boolean };

export const useSupervisionSettings = () =>
  useQuery({
    queryKey: ['supervision-settings'],
    queryFn: () => api<View>('/supervision/settings'),
    staleTime: 60_000,
    retry: false,
  });

export function SettingsPanel() {
  const qc = useQueryClient();
  const q = useSupervisionSettings();
  const [list, setList] = useState<RemarkSource[]>([]);
  useEffect(() => {
    if (q.data) setList(q.data.internalSources);
  }, [q.data]);
  const m = useMutation({
    mutationFn: () => api('/supervision/settings', { method: 'PUT', body: { internalSources: list } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['supervision-settings'] });
      toast('Настройки сохранены');
    },
  });
  if (!q.data?.canEdit) return null;
  const changed = [...list].sort().join() !== [...q.data.internalSources].sort().join();
  return (
    <Panel title="Внутренние источники замечаний" className="mt-4">
      <p className="mb-2 text-[14px] text-ink-2">
        Замечания этих источников видят только сотрудники компании — подрядчик, технадзор и проектировщик их
        не видят. Настройка действует для новых замечаний.
      </p>
      <div className="grid gap-1 sm:grid-cols-2">
        {(Object.keys(REMARK_SOURCES) as RemarkSource[])
          // Технадзор и авторский надзор выдают внешние участники — внутренними они не бывают
          .filter((k) => k !== 'tech_supervision' && k !== 'author_supervision')
          .map((k) => (
            <Checkbox
              key={k}
              className="min-h-11 items-center md:min-h-0"
              checked={list.includes(k)}
              onChange={(e) => setList((x) => (e.target.checked ? [...x, k] : x.filter((y) => y !== k)))}
              label={REMARK_SOURCES[k]}
            />
          ))}
      </div>
      <div className="mt-3">
        <Button
          variant="primary"
          className="h-11 md:h-10"
          disabled={!changed}
          loading={m.isPending}
          onClick={() => m.mutate()}
        >
          Сохранить
        </Button>
      </div>
      <ErrorBox error={m.error} className="mt-2" />
    </Panel>
  );
}
